import { Fragment, useCallback, useMemo, useRef, useState, type ReactNode } from 'react';
import type { SxProps, Theme } from '@mui/material';
import {
  Box,
  Card,
  Checkbox,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Snackbar,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import ViewColumnOutlinedIcon from '@mui/icons-material/ViewColumnOutlined';
import DensitySmallIcon from '@mui/icons-material/DensitySmall';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import CheckIcon from '@mui/icons-material/Check';
import { Pagination } from './Pagination';
import { LoadingSkeleton } from './LoadingSkeleton';
import { EmptyState } from './EmptyState';
import { ErrorPanel } from './ErrorPanel';
import { useGridView } from './useGridView';
import { DENSITY_PADDING, type Density, type GridView } from './gridView';
import { toCsv, downloadCsv } from '../../utils/csv';

export interface Column<T> {
  key: string;
  header: string;
  /**
   * A header that is a control rather than a label — a select-all checkbox.
   *
   * `header` stays a plain string alongside it, because the column-visibility
   * menu, the CSV export and the copy-row shortcut all need something readable.
   * Widening `header` itself would have made those three places render a
   * checkbox into a filename.
   */
  headerRender?: () => ReactNode;
  /**
   * An explanation rendered BESIDE the header, not instead of it.
   *
   * Separate from `headerRender` because that replaces the cell's content and
   * therefore the sort control — a help icon on a sortable column would have
   * silently removed the ability to sort by it.
   */
  headerHelp?: ReactNode;
  render: (row: T) => ReactNode;
  sortable?: boolean;
  width?: number | string;
  minWidth?: number;
  align?: 'left' | 'right' | 'center';
  /** Plain value for CSV export / copy. Falls back to string renders when omitted. */
  exportValue?: (row: T) => string | number;
  /** Prevent this column from being hidden (e.g. the primary identifier). */
  disableHide?: boolean;
}

interface DataGridProps<T> {
  columns: Column<T>[];
  rows: T[];
  getRowId: (row: T) => string;
  loading?: boolean;
  error?: unknown;
  onRetry?: () => void;
  sort?: string;
  direction?: 'asc' | 'desc';
  onSortChange?: (key: string) => void;
  /*
   * ⚠️ **PAGING IS OPTIONAL, because not every list HAS a page count.** The log
   * store pages by KEYSET — `(at, id) < (…)` — precisely so it never has to count
   * matching rows, which over a partitioned table with a full-text predicate
   * costs about as much as the page itself. There is no total, so a pager reading
   * "Page 1 of 1" would be a confident lie, and `totalPages` falling back to 1 is
   * exactly how the SearchBox paging defect presented. Omit them and no pager is
   * rendered; the caller supplies its own "load more".
   */
  page?: number;
  pageSize?: number;
  totalItems?: number;
  totalPages?: number;
  onPage?: (page: number) => void;
  onPageSize?: (size: number) => void;
  onRowClick?: (row: T) => void;
  /**
   * Per-row styling, for lists where some rows warrant more visual weight than
   * others. Added so callers do not have to fake it inside the first cell,
   * which cannot span the row height and breaks the moment a column is
   * reordered or hidden.
   */
  rowSx?: (row: T) => SxProps<Theme>;
  /**
   * Extra content under a row, spanning every visible column.
   *
   * Return `null` for a row that is not expanded — which is also what makes the
   * content LAZY: an unexpanded row's panel is never mounted, so it fetches
   * nothing. The grid deliberately does not own the open state; which row is
   * open is a decision about the list, and only the caller knows whether
   * something else on screen (a bulk selection, a filter change) should close
   * it.
   */
  renderExpanded?: (row: T) => ReactNode;
  toolbar?: ReactNode;
  emptyMessage?: string;
  /** Persist column/density view under this key. */
  persistKey?: string;
  /** Controlled view (used by Saved Views). Overrides persistKey. */
  view?: GridView;
  onViewChange?: (v: GridView) => void;
  /** Show density / columns / export tools (default true). */
  enableTools?: boolean;
  exportFilename?: string;
  /**
   * REPLACES the built-in export rather than hiding it.
   *
   * ⚠️ The built-in one writes the rows currently LOADED, which is right for a
   * list that shows its whole page and wrong wherever the list is a window onto
   * something larger — there it produces a file that looks complete and is not,
   * the silent-truncation failure `vehiclesTruncated` exists to prevent and the
   * one the log export refuses outright above its cap. A caller that can export
   * the FULL result set passes it here, so the control stays exactly where a
   * reader has learnt to find it on every other list and simply does the honest
   * thing on this one.
   */
  onExport?: () => void;
  stickyFirstColumn?: boolean;
  /** Accessible name for the table. */
  label?: string;
}

function cellText(node: ReactNode): string {
  return typeof node === 'string' || typeof node === 'number' ? String(node) : '';
}

export function DataGrid<T>(props: DataGridProps<T>) {
  const {
    columns,
    rows,
    getRowId,
    loading,
    error,
    sort,
    direction,
    onSortChange,
    onRowClick,
    renderExpanded,
    rowSx,
    enableTools = true,
  } = props;

  const keys = useMemo(() => columns.map((c) => c.key), [columns]);
  const byKey = useMemo(() => new Map(columns.map((c) => [c.key, c])), [columns]);
  const [view, setView] = useGridView(keys, {
    persistKey: props.persistKey,
    view: props.view,
    onViewChange: props.onViewChange,
  });

  const visible = useMemo(
    () =>
      view.order
        .map((k) => byKey.get(k))
        .filter((c): c is Column<T> => !!c && !view.hidden.includes(c.key)),
    [view.order, view.hidden, byKey],
  );

  const pad = DENSITY_PADDING[view.density];
  const [copied, setCopied] = useState(false);

  /*
   * Below `sm` the table becomes a list of cards.
   *
   * A twelve-column table in a 390px window is one column of content and a
   * horizontal scrollbar the reader cannot see is there — so the other eleven
   * columns are, in practice, missing. Cards show every visible column at the
   * cost of vertical space, which a phone has and horizontal space it does not.
   *
   * Deliberately keyed to viewport width rather than to a prop: every list in
   * the product goes through here, and making each page opt in would guarantee
   * that some never did.
   */
  const theme = useTheme();
  const asCards = useMediaQuery(theme.breakpoints.down('sm'));
  const sortable = useMemo(() => visible.filter((c) => c.sortable), [visible]);

  /*
   * The column that names the row, promoted to the card's heading.
   *
   * Taken from `disableHide` — already documented as "the primary identifier" —
   * rather than from column order. Ordering does not carry that meaning: the
   * incident queue leads with a risk CHIP, so heading the card with the first
   * column produced a card titled "High" with the incident title demoted to a
   * labelled field below it.
   */
  const primaryKey = useMemo(
    () => visible.find((c) => c.disableHide)?.key ?? visible[0]?.key,
    [visible],
  );

  // ── column tools ────────────────────────────────────────────────────────
  const [densityAnchor, setDensityAnchor] = useState<HTMLElement | null>(null);
  const [columnsAnchor, setColumnsAnchor] = useState<HTMLElement | null>(null);

  const setDensity = (density: Density) => {
    setView((v) => ({ ...v, density }));
    setDensityAnchor(null);
  };
  const toggleColumn = (key: string) =>
    setView((v) => ({
      ...v,
      hidden: v.hidden.includes(key) ? v.hidden.filter((k) => k !== key) : [...v.hidden, key],
    }));

  // ── reorder (drag) ───────────────────────────────────────────────────────
  const dragKey = useRef<string | null>(null);
  const onDrop = (targetKey: string) => {
    const from = dragKey.current;
    dragKey.current = null;
    if (!from || from === targetKey) return;
    setView((v) => {
      const order = [...v.order];
      const fromIdx = order.indexOf(from);
      const toIdx = order.indexOf(targetKey);
      if (fromIdx < 0 || toIdx < 0) return v;
      order.splice(fromIdx, 1);
      order.splice(toIdx, 0, from);
      return { ...v, order };
    });
  };

  // ── resize ───────────────────────────────────────────────────────────────
  const startResize = useCallback(
    (key: string, startX: number, startWidth: number) => {
      const onMove = (e: MouseEvent) => {
        const width = Math.max(64, startWidth + (e.clientX - startX));
        setView((v) => ({ ...v, widths: { ...v.widths, [key]: width } }));
      };
      const onUp = () => {
        document.removeEventListener('mousemove', onMove);
        document.removeEventListener('mouseup', onUp);
      };
      document.addEventListener('mousemove', onMove);
      document.addEventListener('mouseup', onUp);
    },
    [setView],
  );

  // ── export + copy ──────────────────────────────────────────────────────────
  const exportCsv = () => {
    const headers = visible.map((c) => c.header);
    const data = rows.map((row) =>
      visible.map((c) => (c.exportValue ? c.exportValue(row) : cellText(c.render(row)))),
    );
    downloadCsv(props.exportFilename ?? 'export', toCsv(headers, data));
  };
  // A caller that can export the whole result set wins; see `onExport`.
  const doExport = props.onExport ?? exportCsv;
  const copyRow = (row: T) => {
    const tsv = visible
      .map((c) => (c.exportValue ? c.exportValue(row) : cellText(c.render(row))))
      .join('\t');
    try {
      void navigator.clipboard?.writeText(tsv);
      setCopied(true);
    } catch {
      /* clipboard unavailable */
    }
  };

  const widthFor = (c: Column<T>) => view.widths[c.key] ?? c.width;
  const stickyFirst = (i: number): object =>
    props.stickyFirstColumn && i === 0
      ? { position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper' }
      : {};

  return (
    <Card sx={{ display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {(props.toolbar || enableTools) && (
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            p: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexWrap: 'wrap',
          }}
        >
          {props.toolbar}
          <Box sx={{ flex: 1 }} />
          {enableTools && (
            <>
              <Tooltip title="Row density">
                <IconButton
                  size="small"
                  onClick={(e) => setDensityAnchor(e.currentTarget)}
                  aria-label="Row density"
                >
                  <DensitySmallIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Columns">
                <IconButton
                  size="small"
                  onClick={(e) => setColumnsAnchor(e.currentTarget)}
                  aria-label="Choose columns"
                >
                  <ViewColumnOutlinedIcon fontSize="small" />
                </IconButton>
              </Tooltip>
              <Tooltip title="Export CSV">
                <span>
                  <IconButton
                    size="small"
                    onClick={doExport}
                    aria-label="Export CSV"
                    disabled={rows.length === 0}
                  >
                    <FileDownloadOutlinedIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
            </>
          )}
        </Box>
      )}

      <Menu anchorEl={densityAnchor} open={!!densityAnchor} onClose={() => setDensityAnchor(null)}>
        {(['compact', 'standard', 'comfortable'] as Density[]).map((d) => (
          <MenuItem key={d} onClick={() => setDensity(d)} selected={view.density === d}>
            <ListItemIcon>
              {view.density === d ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText primary={d[0]!.toUpperCase() + d.slice(1)} />
          </MenuItem>
        ))}
      </Menu>

      <Menu anchorEl={columnsAnchor} open={!!columnsAnchor} onClose={() => setColumnsAnchor(null)}>
        {columns.map((c) => (
          <MenuItem
            key={c.key}
            dense
            onClick={() => !c.disableHide && toggleColumn(c.key)}
            disabled={c.disableHide}
          >
            <Checkbox
              size="small"
              edge="start"
              checked={!view.hidden.includes(c.key)}
              tabIndex={-1}
              disableRipple
            />
            <ListItemText primary={c.header} />
          </MenuItem>
        ))}
      </Menu>

      {asCards && sortable.length > 0 && !loading && !error && rows.length > 0 && (
        // The column headers carried sorting, and they are gone here. Without
        // this the capability would vanish on a phone with nothing to say so.
        <Box
          sx={{
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            // One line that scrolls. Wrapped, it read as a second header row
            // rather than as controls.
            overflowX: 'auto',
            whiteSpace: 'nowrap',
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0 }}>
            Sort
          </Typography>
          {sortable.map((c) => (
            <TableSortLabel
              key={c.key}
              active={sort === c.key}
              direction={sort === c.key ? direction : 'asc'}
              onClick={() => onSortChange?.(c.key)}
              sx={{ fontSize: '0.8rem', flexShrink: 0 }}
            >
              {c.header}
            </TableSortLabel>
          ))}
        </Box>
      )}

      {asCards ? (
        <Box sx={{ flex: 1 }}>
          {!loading && !error && rows.length > 0 && (
            <Box component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
              {rows.map((row, rowIndex) => (
                <Box
                  component="li"
                  key={getRowId(row)}
                  onClick={() => onRowClick?.(row)}
                  onKeyDown={(e) => {
                    if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      onRowClick(row);
                    }
                  }}
                  tabIndex={onRowClick ? 0 : undefined}
                  role={onRowClick ? 'button' : undefined}
                  sx={{
                    px: 2,
                    py: 1.5,
                    borderBottom: 1,
                    borderColor: 'divider',
                    cursor: onRowClick ? 'pointer' : 'default',
                    /*
                     * ⚠️ The CARD view is striped too, or the same list would
                     * be banded on a laptop and flat on a phone — a difference
                     * a reader would read as a different screen. Same tint,
                     * same data index, so the two views cannot disagree about
                     * which rows are shaded.
                     */
                    ...(rowIndex % 2 === 1 && {
                      bgcolor: (th: Theme) =>
                        th.palette.mode === 'dark'
                          ? 'rgba(255, 255, 255, 0.035)'
                          : 'rgba(0, 0, 0, 0.022)',
                    }),
                    ...(rowSx?.(row) as object),
                  }}
                >
                  {/* Identity first, whatever column order says. */}
                  {[...visible]
                    .sort((a, b) => (a.key === primaryKey ? -1 : b.key === primaryKey ? 1 : 0))
                    .map((c) => {
                      const isPrimary = c.key === primaryKey;
                      return (
                        <Box
                          key={c.key}
                          sx={{
                            display: 'flex',
                            gap: 1.5,
                            alignItems: 'baseline',
                            py: 0.25,
                            ...(isPrimary ? { mb: 0.5 } : {}),
                          }}
                        >
                          {!isPrimary && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              sx={{ minWidth: 104, flexShrink: 0 }}
                            >
                              {c.header}
                            </Typography>
                          )}
                          <Box sx={{ flex: 1, minWidth: 0 }}>{c.render(row)}</Box>
                        </Box>
                      );
                    })}
                  {/*
                    The expanded panel reaches the CARD view too.
                    Rendering it only in the table would leave the expander
                    toggling state on a phone and showing nothing — a control
                    that appears to do nothing, which reads as a broken product
                    rather than a missing branch.
                  */}
                  {renderExpanded?.(row) && (
                    <Box sx={{ mx: -2, mt: 1.5 }} onClick={(e) => e.stopPropagation()}>
                      {renderExpanded(row)}
                    </Box>
                  )}
                </Box>
              ))}
            </Box>
          )}
          {loading && <LoadingSkeleton rows={6} />}
          {!loading && !!error && <ErrorPanel error={error} onRetry={props.onRetry} />}
          {!loading && !error && rows.length === 0 && (
            <EmptyState
              title="No results"
              message={props.emptyMessage ?? 'Try adjusting your filters or search.'}
            />
          )}
        </Box>
      ) : (
        <TableContainer sx={{ flex: 1 }}>
          <Table stickyHeader size="small" aria-label={props.label}>
            <TableHead>
              <TableRow>
                {visible.map((c, i) => (
                  <TableCell
                    key={c.key}
                    align={c.align}
                    draggable
                    onDragStart={() => (dragKey.current = c.key)}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={() => onDrop(c.key)}
                    sx={{
                      width: widthFor(c),
                      minWidth: c.minWidth,
                      whiteSpace: 'nowrap',
                      position: 'relative',
                      cursor: 'grab',
                      userSelect: 'none',
                      ...stickyFirst(i),
                    }}
                  >
                    {c.headerRender ? (
                      c.headerRender()
                    ) : c.sortable && onSortChange ? (
                      <TableSortLabel
                        active={sort === c.key}
                        direction={sort === c.key ? direction : 'asc'}
                        onClick={() => onSortChange(c.key)}
                      >
                        {c.header}
                      </TableSortLabel>
                    ) : (
                      c.header
                    )}
                    {c.headerHelp}
                    <Box
                      role="separator"
                      aria-label={`Resize ${c.header}`}
                      onMouseDown={(e) => {
                        e.preventDefault();
                        const current = (e.currentTarget.parentElement as HTMLElement).offsetWidth;
                        startResize(c.key, e.clientX, current);
                      }}
                      sx={{
                        position: 'absolute',
                        top: 0,
                        right: 0,
                        height: '100%',
                        width: 6,
                        cursor: 'col-resize',
                        '&:hover': { bgcolor: 'primary.main', opacity: 0.4 },
                      }}
                    />
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            {!loading && !error && rows.length > 0 && (
              <TableBody>
                {rows.map((row, rowIndex) => {
                  const expanded = renderExpanded?.(row) ?? null;
                  /*
                   * ⚠️ **Striped on the DATA index, never `:nth-of-type`.** An
                   * expanded panel is its own `<tr>`, so a CSS rule counting DOM
                   * rows flips the pattern for everything below an open one — the
                   * stripe would move as a reader opened and closed rows, which
                   * is worse than no stripe because it looks like the list
                   * reordered itself.
                   */
                  const striped = rowIndex % 2 === 1;
                  return (
                    <Fragment key={getRowId(row)}>
                      <TableRow
                        hover={!!onRowClick}
                        sx={{
                          /*
                           * ⚠️ A tint of the SURFACE, carrying no meaning. Green,
                           * amber and red are the four risk levels on every badge and
                           * every score here, so a coloured stripe would claim a
                           * severity for every other row. Deliberately WEAKER than
                           * `action.hover` (0.04 light / 0.08 dark): at the same
                           * strength a hovered odd row would look identical to an
                           * even one, so the hover would silently stop working on
                           * half the table.
                           */
                          ...(striped && {
                            bgcolor: (th: Theme) =>
                              th.palette.mode === 'dark'
                                ? 'rgba(255, 255, 255, 0.035)'
                                : 'rgba(0, 0, 0, 0.022)',
                          }),
                          /* Hover must still win on a striped row. */
                          ...(striped && onRowClick && { '&:hover': { bgcolor: 'action.hover' } }),
                          /* ⚠️ The caller's own row styling stays LAST: a page that
                       marks a row deliberately must beat the decoration. */
                          ...(rowSx?.(row) as object),
                        }}
                        onClick={() => onRowClick?.(row)}
                        onContextMenu={(e) => {
                          e.preventDefault();
                          copyRow(row);
                        }}
                        tabIndex={onRowClick ? 0 : undefined}
                        onKeyDown={(e) => {
                          if (onRowClick && (e.key === 'Enter' || e.key === ' ')) {
                            e.preventDefault();
                            onRowClick(row);
                          }
                          if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'c') copyRow(row);
                        }}
                      >
                        {visible.map((c, i) => (
                          <TableCell
                            key={c.key}
                            align={c.align}
                            sx={{ width: widthFor(c), paddingBlock: `${pad}px`, ...stickyFirst(i) }}
                          >
                            {c.render(row)}
                          </TableCell>
                        ))}
                      </TableRow>
                      {expanded && (
                        <TableRow>
                          <TableCell
                            colSpan={visible.length}
                            sx={{ p: 0, borderBottom: '1px solid', borderColor: 'divider' }}
                            /*
                             * The panel is not part of the row's click target. Without
                             * this, pressing a button inside it would ALSO fire
                             * `onRowClick` and navigate away mid-decision — the row
                             * opens the detail page, and a control inside it must not.
                             */
                            onClick={(e) => e.stopPropagation()}
                          >
                            {expanded}
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
              </TableBody>
            )}
          </Table>
          {loading && <LoadingSkeleton rows={8} />}
          {!loading && !!error && <ErrorPanel error={error} onRetry={props.onRetry} />}
          {!loading && !error && rows.length === 0 && (
            <EmptyState
              title="No results"
              message={props.emptyMessage ?? 'Try adjusting your filters or search.'}
            />
          )}
        </TableContainer>
      )}

      {/*
        ⚠️ Rendered only when the caller HAS a page count. A list paged by keyset
        has no total by design, and a pager defaulting to "1 of 1" is a lie the
        reader acts on — that is precisely how the SearchBox defect presented, as
        a truthful-looking "Page 1 / 1" rendered before any data existed.
      */}
      {props.page !== undefined &&
        props.pageSize !== undefined &&
        props.onPage &&
        props.onPageSize && (
          <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
            <Pagination
              page={props.page}
              pageSize={props.pageSize}
              totalItems={props.totalItems ?? 0}
              totalPages={props.totalPages ?? 1}
              onPage={props.onPage}
              onPageSize={props.onPageSize}
            />
          </Box>
        )}

      <Snackbar
        open={copied}
        autoHideDuration={1500}
        onClose={() => setCopied(false)}
        message="Row copied to clipboard"
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
      />
    </Card>
  );
}
